import type { AnyAction, Reducer } from 'redux';
import type { PickByValue } from 'types/utility';
import type { Action, ActionCreator } from 'typescript-fsa';
import { actionCreatorFactory } from 'typescript-fsa';
import { reducerWithInitialState } from 'typescript-fsa-reducers';

export interface ToggleSelectionParams<
  TSelection extends {},
  TKey extends keyof PickByValue<TSelection, Array<number>>,
> {
  selectionKey: TKey;
  value?: number | Array<number>;
  batchRemove?: boolean;
}

export interface SetSelectionParams<TSelection extends {}, TKey extends keyof TSelection> {
  selectionKey: TKey;
  value: TSelection[TKey] | ((currentSelection: TSelection[TKey]) => TSelection[TKey]);
}

export interface SelectionState<T> {
  selection: T;
}

export interface BatchActionsModule<TSelection extends {}> {
  withBatchSelectionsReducer: <S extends SelectionState<TSelection>>(
    reducer: Reducer<S, AnyAction>
  ) => Reducer<S, AnyAction>;
  batchSelectionActions: BatchActions<TSelection>;
}
interface BatchActions<TSelection extends {}> {
  toggleSelection: <TKey extends keyof PickByValue<TSelection, Array<number>>>(
    payload: ToggleSelectionParams<TSelection, TKey>
  ) => Action<ToggleSelectionParams<TSelection, TKey>>;
  setSelection: <TKey extends keyof TSelection>(
    payload: SetSelectionParams<TSelection, TKey>
  ) => Action<SetSelectionParams<TSelection, TKey>>;
  clearSelection: ActionCreator<void>;
}

export function createBatchActionsModule<TSelection extends {}>(
  prefix: string
): BatchActionsModule<TSelection> {
  const actionCreator = actionCreatorFactory(`timely/${prefix}`);

  const toggleSelectionAction = actionCreator<
    ToggleSelectionParams<TSelection, keyof PickByValue<TSelection, Array<number>>>
  >('batchActions/TOGGLE_SELECTION');
  const setSelectionAction = actionCreator<SetSelectionParams<TSelection, keyof TSelection>>(
    'batchActions/SET_SELECTION'
  );
  const clearSelectionAction = actionCreator('batchActions/CLEAR_SELECTION');

  const withBatchSelectionsReducer = <S extends SelectionState<TSelection>>(
    parentReducer: Reducer<S, AnyAction>
  ): Reducer<S, AnyAction> => {
    const initialState = parentReducer(undefined, { type: '__selection_initial_state' });

    const mapSelections =
      <TAction>(
        mapper: (subState: TSelection, action: TAction) => Partial<SelectionState<TAction>>
      ) =>
      (state: S, action: TAction): S => {
        return {
          ...state,
          selection: {
            ...state.selection,
            ...mapper(state.selection, action),
          },
        };
      };
    return reducerWithInitialState(initialState)
      .case(
        setSelectionAction,
        mapSelections((state, payload): TSelection => {
          return {
            ...state,
            [payload.selectionKey]:
              payload.value instanceof Function
                ? payload.value(state[payload.selectionKey])
                : payload.value,
          };
        })
      )
      .case(
        toggleSelectionAction,
        mapSelections((state, payload): TSelection => {
          const { selectionKey, value, batchRemove = false } = payload;
          // This is safe thanks to the external types on toggleSelectionAction
          const array = state[selectionKey] as any as Array<number>;
          // are we receiving more than one selection?
          if (Array.isArray(value)) {
            // are we responding to someone toggling on/off select all?
            if (batchRemove) {
              return {
                ...state,
                // Remove the values on the action from our selections
                [selectionKey]: array.filter(val => !value.includes(val)),
              };
            }
            // otherwise append the new values
            return {
              ...state,
              [selectionKey]: [...array.filter(val => !value.includes(val)), ...value],
            };
          }
          const isSelected = array.some(id => id === value);
          // Add or remove value (id) from current selection
          const selection = isSelected ? array.filter(id => id !== value) : [...array, value];

          return {
            ...state,
            [payload.selectionKey]: selection,
          };
        })
      )
      .case(
        clearSelectionAction,
        mapSelections(() => ({
          ...initialState.selection,
        }))
      )
      .default(parentReducer);
  };
  return {
    withBatchSelectionsReducer,
    batchSelectionActions: {
      // Need to cast here to avoid some typescript-fsa incompatibility. Better than `as any`
      toggleSelection:
        toggleSelectionAction as BatchActionsModule<TSelection>['batchSelectionActions']['toggleSelection'],
      setSelection:
        setSelectionAction as BatchActionsModule<TSelection>['batchSelectionActions']['setSelection'],
      clearSelection: clearSelectionAction,
    },
  };
}
